import { Component, OnInit } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { RepositoryService } from './../../shared/services/repository.service';
import { RiskEvent } from "../../_interfaces/RiskEvent.model";
import { Risk } from "../../_interfaces/risk.model";
import { RiskSummary } from "../../_interfaces/RiskSummary.model";
import { RiskUser } from './../../_interfaces/RiskUser.model';
import { RiskService } from '../../shared/services/risk.service';
import { take } from 'rxjs/operators';
import { combineLatest } from 'rxjs';
import { formatDate } from '@angular/common';

enum EventStatus {
  New = 1,
  Updated = 2,
  RequestedClosed = 3,
  Closed = 4
}

@Component({
  selector: 'app-risk-event',
  templateUrl: './risk-event.component.html',
  styleUrls: ['./risk-event.component.css']
})
export class RiskEventComponent implements OnInit {
  riskEventForm: FormGroup;
  submitted = false;
  fileData: File = null;

  public riskEvent: RiskEvent;
  riskEventLoaded: boolean = false;
  EventStatus: typeof EventStatus = EventStatus;

  public risks: Risk[];
  public selectedRisk: Risk;
  public riskSummary: RiskSummary;

  public user: RiskUser;

  constructor(
    private formBuilder: FormBuilder,
    private repository: RepositoryService,
    private riskService: RiskService,
    private router: Router,
    private activeRoute: ActivatedRoute) { }

  ngOnInit() {
    this.riskEventForm = this.formBuilder.group({
      //eventId: ['', Validators.required],
      eventSummary: ['', Validators.required],
      eventDetails: ['', Validators.required],
      actionsTaken: ['', Validators.required],
      futureActions: [''],
      closureAnalysis: [''],
      cost: ['', Validators.required],
      estimatedLiability: ['', Validators.required],
      eventStatus: [''],
      riskType: [''],
      headlineRisk: [''],
      operationalRiskType: [''],
      financialRiskType: [''],
      riskId: [''],
      mitigationId: [''],
      evidence: [null],
      dateOpened: [''],
      dateClosed: ['']
    });

    this.getRiskEventDetails();

    this.user = this.repository.GetMyUser();
  }
  // convenience getter for easy access to form fields
  get f() { return this.riskEventForm.controls; }

  getRiskClosureDetails(id: number) {
    //  Call service to download pdf as a blob to open in a new window
    let apiUrl: string = `RiskEvents/ClosureEvidence/${id}`;
    this.repository.getFileData(apiUrl)
      .subscribe(res => {
        //  download file and open it
        var file = new Blob([res], { type: "application/pdf" });

        if (window.navigator && window.navigator.msSaveOrOpenBlob) {
          window.navigator.msSaveOrOpenBlob(file, "Closure Evidence.pdf");
        }
        else {
          var objectUrl = URL.createObjectURL(file);
          window.open(objectUrl);
        }
      });
  }

  deleteRiskClosureDetails(id: number) {
    let apiUrl: string = `RiskEvents/ClosureEvidence/${id}`;
    this.repository.delete(apiUrl)
      .subscribe(res => {
        // Update the dialog contents
        this.getRiskEventDetails();
      });
  }

  getRiskEventDetails() {
    let id: string = this.activeRoute.snapshot.params['id'];
    let apiUrl: string = `RiskEvents/${id}`;

    this.repository.getData(apiUrl)
      .subscribe(res => {
        this.riskEvent = res as RiskEvent;
        this.f.eventSummary.setValue(this.riskEvent.name);
        this.f.eventDetails.setValue(this.riskEvent.description);
        this.f.actionsTaken.setValue(this.riskEvent.actionsTaken);
        this.f.futureActions.setValue(this.riskEvent.futureActions);
        this.f.closureAnalysis.setValue(this.riskEvent.closureAnalysis);

        this.f.dateOpened.setValue(formatDate(this.riskEvent.createdAt, 'dd/MM/yyyy', 'en'));
        if (this.riskEvent.deletedAt) {
          this.f.dateClosed.setValue(formatDate(this.riskEvent.deletedAt, 'dd/MM/yyyy', 'en'));
        }
        this.f.cost.setValue(this.riskEvent.cost);
        this.f.estimatedLiability.setValue(this.riskEvent.estimatedLiability);
        if (this.riskEvent.riskId != null) {
          this.getInitialMitigationsForRisk(this.riskEvent.riskId.toString());
        }

        this.riskEventLoaded = true;
      });
  }
  onSubmit() {
    this.submitted = true;

    // stop here if form is invalid
    if (this.riskEventForm.invalid) {
      return;
    }

    // display form values on success
    //alert('SUCCESS!! :-)\n\n' + JSON.stringify(this.riskEventForm.value, null, 4));
    // Save updated Risk Event from form to object
    this.riskEvent.name = this.f.eventSummary.value;
    this.riskEvent.description = this.f.eventDetails.value;
    this.riskEvent.cost = this.f.cost.value;
    this.riskEvent.estimatedLiability = this.f.estimatedLiability.value;
    this.riskEvent.actionsTaken = this.f.actionsTaken.value;

    this.riskEvent.futureActions = this.f.futureActions.value;
    this.riskEvent.closureAnalysis = this.f.closureAnalysis.value;
    if (this.f.riskId.value) {
      this.riskEvent.riskId = + this.f.riskId.value; // Note - the + casts the value to a number. Without the + web api doesn't recognise the object 
    }

    if (this.f.mitigationId.value) {
      this.riskEvent.mitigationId = + this.f.mitigationId.value; // Note - the + casts the value to a number. Without the + web api doesn't recognise the object 
   }

    // Save updated Risk event here
    let apiAddress: string = `RiskEvents`; 

    this.repository.update(apiAddress, this.riskEvent)
      .subscribe(res => {
       // this.riskEvent = res as RiskEvent;
        this.router.navigate(['/myrisks/details/' + this.riskEvent.riskId]);
      }, error => {
        //console.error("Error: ", error);
          alert('Error:!' + error.message +  '\n\n');
      }
    );
  }

  onFileSelect(event) {
    if (event.target.files.length > 0) {
      const file = event.target.files[0];
      if (!file.name.includes(".pdf")) {
        alert('Please select a pdf file to use as evidence!! :-)\n\n');
        return;
      }
      this.f.evidence.setValue(file);
      this.addClosureEvidence(this.riskEvent.id);
    }
  }

  addClosureEvidence(riskEventId: number) {
    this.fileData = (this.f.evidence.value as File);
    let formData = new FormData();
    formData.append('file', this.fileData);

    let apiAddress: string = `RiskEvents/` + riskEventId + `/ClosureEvidence`; 
    //  Save the file on the server and the url in the Closure Evidence table

    this.repository.uploadFile(apiAddress, formData)
      .subscribe(res => {
        // Update the dialog contents
        this.getRiskEventDetails();
      });; 
  }

  onReset() {
    this.submitted = false;
    this.riskEventForm.reset();
  }

  onCancelForm() {
    this.submitted = false;
    this.router.navigate(['/myrisks/details/' + this.riskEvent.riskId]);
  }

  onRequestClosure(riskEventId) {
    // change status to 3
    let apiAddress: string = `RiskEvents/` + riskEventId + `/Status/3`; 
    this.repository.update(apiAddress, null)
      .subscribe(res => {
        this.riskEvent = res as RiskEvent;

      });
  }
  onCancelClosure(riskEventId) {
    //  change status to 2
    let apiAddress: string = `RiskEvents/` + riskEventId + `/Status/2`; 
    this.repository.update(apiAddress, null)
      .subscribe(res => {
        this.riskEvent = res as RiskEvent;
        this.router.navigate(['/myrisks/details/' + this.riskEvent.riskId]);
      });
  }

  onAuthorizeClosure(riskEventId) {
    // change status to 4
    let apiAddress: string = `RiskEvents/` + riskEventId + `/Status/4`; 
    this.repository.update(apiAddress, null)
      .subscribe(res => {
        this.riskEvent = res as RiskEvent;
        this.router.navigate(['/myrisks/details/' + this.riskEvent.riskId]);
      });
  }

  // getMitigationsForRisk
  public getMitigationsForRisk(RiskId: string) {
    // this.selectedRisk = RiskId;

    let id: string = RiskId;
    let apiUrl: string = `RiskSummary?id=${id}`;

    this.repository.getData(apiUrl)
      .subscribe(res => {
        this.riskSummary = res as RiskSummary;


      });
  }
  public getInitialMitigationsForRisk(RiskId: string) {
    // this.selectedRisk = RiskId;

    let id: string = RiskId;
    let apiUrl: string = `RiskSummary?id=${id}`;


    this.repository.getData(apiUrl).pipe(
      take(1)
    ).subscribe((response) => {
      this.riskSummary = response as RiskSummary;

      combineLatest(
        this.riskService.getRiskTypeDetails(this.riskSummary.riskType),
        this.riskService.getHeadlineRiskDetails(this.riskSummary)
      ).subscribe(([riskType, headlineRisk]) => {
        this.f.riskType.setValue(riskType.name);
        this.f.headlineRisk.setValue(headlineRisk.name);
      });
       
      this.f.riskId.setValue(this.riskEvent.riskId.toString());

      if (this.riskEvent.mitigationId != null) {
        this.f.mitigationId.setValue(this.riskEvent.mitigationId.toString());
      }
    });
  }
}
